import { createEffect, createMemo, onCleanup, onMount, For, Show } from "solid-js";
import { createSpring } from "../lib/spring";
import { selection } from "../lib/haptics";
import { spriteHrefFor } from "../lib/logos";

const ITEM_H = 88;
/**
 * The strip renders 3 copies of items. The spring wraps `y` modulo
 * `items.length * ITEM_H`, so visually the strip scrolls forever
 * but DOM stays at 3× the data, typically 16×3 = 48 nodes
 * versus the old 16×14 = 224 nodes.
 */
const STRIP_COPIES = 3;

/**
 * One of three render shapes per reel:
 *   - "logo"        : show a coloured brand logo + small label (reel 1).
 *   - "icon-label"  : show a big emoji + uppercase label (reels 2 & 3).
 *   - "text"        : original text-only behaviour (kept as a fallback).
 */
export type ReelKind = "logo" | "icon-label" | "text";

interface ReelProps {
  items: string[];
  finalIndex: number;
  /** Flip true → strip flies; flip false → spring snaps onto finalIndex. */
  spinning: boolean;
  /** Strip scroll speed in px/sec while spinning. */
  speed?: number;
  /** Visual rendering style. */
  kind?: ReelKind;
  /** Apply "anticipation" pulse, used when LLM is late on the third reel. */
  anticipate?: boolean;
  /** Highlight the centre row (winning row), chromatic aberration pop. */
  chroma?: boolean;
}

export function Reel(props: ReelProps) {
  let stripRef: HTMLDivElement | undefined;

  const stripPx = createMemo(() => props.items.length * ITEM_H);

  // The viewport is 3 rows and the side arrows aim at the MIDDLE row, so
  // finalIndex has to resolve into row 1 (centre), not row 0 (top). Both
  // setIdle and setLand need the same offset, keep the math in one place.
  const targetFor = (i: number): number =>
    -(ITEM_H * (props.items.length + i - 1));

  // Render 3 copies of items so modulo wrap is invisible.
  const displayItems = createMemo(() => {
    const xs: string[] = [];
    for (let r = 0; r < STRIP_COPIES; r++) xs.push(...props.items);
    return xs;
  });

  let spring: ReturnType<typeof createSpring> | undefined;
  let lastCell = 0;

  onMount(() => {
    if (!stripRef) return;
    spring = createSpring(
      (y) => {
        stripRef!.style.transform = `translate3d(0, ${y}px, 0)`;
      },
      0,
      stripPx(),
    );
    spring.onTick = (y) => {
      const cell = Math.floor(-y / ITEM_H);
      if (cell !== lastCell) {
        lastCell = cell;
        selection();
      }
    };
    spring.setIdle(targetFor(props.finalIndex));
  });

  // React to spinning prop.
  createEffect(() => {
    if (!spring) return;
    const speed = props.speed ?? 6000;
    if (props.spinning) {
      spring.setSpin(speed);
    } else {
      const cur = spring.position();
      const L = props.items.length;
      const cycle = ITEM_H * L;
      let target = targetFor(props.finalIndex);
      const diff = target - cur;
      // Pick the closest fold that keeps the winning cell INSIDE the
      // rendered strip range. targetFor() points at strip-cell index
      // `L + finalIndex` (the middle copy with STRIP_COPIES=3); each unit
      // of `folded` shifts that index by ±L. Without the clamp,
      // Math.round can pick a fold that drops the winning cell outside
      // [0, STRIP_COPIES*L - 1], the strip is finite, so cells beyond
      // that just don't exist and the reel lands on visible whitespace
      // (P0: 2026-05-15 "middle reel shows empty cells when wildness is
      // near the end of the array").
      const naive = Math.round(diff / cycle);
      const winningCell = L + props.finalIndex;
      const maxFolded = Math.floor(winningCell / L);
      const minFolded = Math.ceil((winningCell - (STRIP_COPIES * L - 1)) / L);
      const folded = Math.max(minFolded, Math.min(maxFolded, naive));
      target -= folded * cycle;
      spring.setLand(target, {
        stiffness: 320,
        damping: 26,
        mass: 0.8,
        restDelta: 0.3,
        restSpeed: 0.5,
      });
    }
  });

  onCleanup(() => spring?.destroy());

  const kind = (): ReelKind => props.kind ?? "text";

  return (
    <div
      class="reel-viewport relative flex-1"
      style={{
        height: `${ITEM_H * 3}px`,
        background: "linear-gradient(180deg, #ffffff 0%, #fafaf6 50%, #ffffff 100%)",
      }}
    >
      <div
        ref={stripRef}
        class="reel-strip absolute left-0 right-0"
        classList={{ "reel-anticipate": !!props.anticipate }}
      >
        <For each={displayItems()}>
          {(item) => <ReelCell item={item} kind={kind()} chroma={!!props.chroma} />}
        </For>
      </div>

      {/* Fade masks, pure compositor, no JS. */}
      <div
        class="pointer-events-none absolute left-0 right-0 top-0"
        style={{
          height: `${ITEM_H}px`,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)",
        }}
      />
      <div
        class="pointer-events-none absolute left-0 right-0 bottom-0"
        style={{
          height: `${ITEM_H}px`,
          background:
            "linear-gradient(0deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)",
        }}
      />
    </div>
  );
}

/**
 * Single cell. Three shapes:
 *   - logo: <svg><use /></svg> for the coloured brand sprite + tiny text below.
 *   - icon-label: emoji big, label small uppercase (used for wildness / temp).
 *   - text: legacy text-only display.
 */
function ReelCell(props: { item: string; kind: ReelKind; chroma: boolean }) {
  if (props.kind === "logo") {
    const href = () => spriteHrefFor(props.item);
    return (
      <div
        class="flex flex-col items-center justify-center"
        style={{ height: `${ITEM_H}px`, padding: "0 4px", gap: "4px" }}
      >
        <Show
          when={href()}
          fallback={
            <div
              class="serif"
              classList={{ chroma: props.chroma }}
              style={{
                "font-size": "14px",
                "font-weight": 600,
                color: "#1a1a1a",
              }}
            >
              {props.item}
            </div>
          }
        >
          {(hr) => (
            <svg
              width="40"
              height="40"
              style={{ display: "block" }}
              aria-hidden="true"
            >
              <use href={hr()} />
            </svg>
          )}
        </Show>
        <div
          classList={{ chroma: props.chroma }}
          style={{
            "font-size": "10.5px",
            "font-weight": 600,
            color: "#1a1a1a",
            "line-height": 1.05,
            "letter-spacing": "0.2px",
            "max-width": "100%",
            overflow: "hidden",
            "text-overflow": "ellipsis",
            "white-space": "nowrap",
          }}
        >
          {props.item}
        </div>
      </div>
    );
  }

  if (props.kind === "icon-label") {
    // Item formatted as "{emoji} {LABEL}", split into two visual rows.
    const parts = (): { icon: string; label: string } => {
      const m = props.item.match(/^(\S+)\s+(.*)$/);
      if (!m) return { icon: "", label: props.item };
      return { icon: m[1], label: m[2] };
    };
    return (
      <div
        class="flex flex-col items-center justify-center"
        style={{ height: `${ITEM_H}px`, padding: "0 4px", gap: "2px" }}
      >
        <div
          style={{
            "font-size": "30px",
            "line-height": 1,
            // Crisp emoji rendering on iOS by avoiding sub-pixel filters.
            "font-variant-emoji": "emoji" as any,
          }}
        >
          {parts().icon}
        </div>
        <div
          classList={{ chroma: props.chroma }}
          style={{
            "font-size": "10.5px",
            "font-weight": 700,
            color: "#1a1a1a",
            "letter-spacing": "1.5px",
            "line-height": 1,
          }}
        >
          {parts().label}
        </div>
      </div>
    );
  }

  // Default text-only cell.
  return (
    <div
      class="flex flex-col items-center justify-center"
      style={{ height: `${ITEM_H}px`, padding: "0 4px", gap: "4px" }}
    >
      <div
        class="serif text-center"
        classList={{ chroma: props.chroma }}
        style={{
          "font-size": "22px",
          "font-weight": 500,
          color: "#1a1a1a",
          "line-height": 1.05,
          "letter-spacing": "-0.2px",
          "max-width": "100%",
          overflow: "hidden",
          "text-overflow": "ellipsis",
          "white-space": "nowrap",
        }}
      >
        {props.item}
      </div>
    </div>
  );
}
